<template>
    <div>
        <p class="has-text-white mt-6" v-if="!conference.id"><i class="fas fa-spin fa-spinner fa-fw mr-3"></i>Please wait. Loading conference information...</p>
        <div v-else>
            <div class="content mt-6">
                <div class="columns is-gapless">
                    <div class="column">
                        <h4 class="mb-0 has-text-white">{{ conference.org }}</h4>
                        <h1 class="mt-2 has-text-white">{{ conference.title }}</h1>
                    </div>
                </div>
            </div>

            <div class="conference conf-info content">
                <div class="logo-frame" v-if="conference.logo">
                    <img :src="conference.logo"/>
                </div>

                <div class="details-box">
                    <div class="detail">
                        <div class="header">Dates</div>
                        <div class="info">{{ confDates }}</div>
                    </div>
                    <div class="detail" v-if="!conference.is_access_only">
                        <div class="header">Fees</div>
                        <div class="info">{{ fees | currency }}</div>
                    </div>
                    <div class="detail">
                        <div class="header">Location</div>
                        <div class="info">{{ conference.location }}</div>
                    </div>
                </div>

                <b-button v-if="!closed" type="is-primary" expanded size="is-large" class="mt-5 mb-3" @click="scrollTo('register')">Register Now<i class="far fa-long-arrow-right ml-3"></i></b-button>

                <div v-else class="closed-box">
                    <div class="columns is-gapless">
                        <div class="column is-narrow"><i class="far fa-ban mr-4"></i></div>
                        <div class="column">
                            <div class="has-text-weight-bold">REGISTRATION CLOSED</div>
                            <div class="meta">Registration Open {{ regDates }}</div>
                        </div>
                    </div>
                </div>

                <div v-html="conference.description" class="content mt-6"></div>

                <hr/>
                <div>
                    <b-button type="is-info" tag="a" :href="'mailto:' + conference.support_email + '?subject=' + conference.title"><i class="far fa-envelope mr-2"></i><strong>Questions?</strong> Contact Organizer</b-button>
                </div>
            </div>

            <div v-if="!closed" ref="register">
                <div class="content mt-6 pt-6">
                    <div class="columns is-gapless">
                        <div class="column is-narrow has-text-white is-hidden-mobile"><i class="far fa-arrow-right-long fa-2x mr-5"></i></div>
                        <div class="column">
                            <h1 class="has-text-white mb-2">Register for the Conference</h1>
<!--                            <div class="has-text-white is-size-5"><span class="has-text-weight-bold">{{ conference.title }}</span>, {{ conference.org }}</div>-->
                        </div>
                    </div>
                </div>

                <div class="conference">
                    <section v-if="!conference.registered">
                        <div class="content">
                            <h4 class="pt-3 mb-4">Registration Type <span class="required">*</span></h4>

                            <div class="columns">
                                <div class="column">
                                    <b-select v-model="registration.regType" expanded>
                                        <option v-for="type in conference.regTypes" :value="type" v-if="!type.is_hidden">{{ type.title }}<span v-if="conference.feeRange[1] > 0"> ({{ type.fee | currency }})</span></option>
                                    </b-select>
                                </div>
                            </div>

                            <div v-if="conference.questions && conference.questions.length">
                                <h4 class="mt-6 mb-1">Additional Information</h4>
                                <p class="mb-4">Additional information requested by event organizer. Questions marked <span class="required">*</span> are required to complete registration.</p>

                                <div class="mt-2" v-for="field in registration.questions">
                                    <div class="mt-5" v-if="field.type === 'text'">
                                        <b-field>
                                            <template #label>{{ field.title }} <span class="required" v-if="field.is_required">*</span></template>
                                            <b-input v-model="field.value" :required="field.is_required === 1"></b-input>
                                            <template #message><span v-html="field.help"></span></template>
                                        </b-field>
                                    </div>
                                    <div class="mt-5" v-else-if="field.type === 'dropdown'">
                                        <b-field>
                                            <template #label>{{ field.title }} <span class="required" v-if="field.is_required">*</span></template>
                                            <b-select v-model="field.value" :required="field.is_required === 1" expanded>
                                                <option v-for="option in JSON.parse(field.options)" :value="option">{{ option }}</option>
                                            </b-select>
                                            <template #message><span v-html="field.help"></span></template>
                                        </b-field>
                                    </div>
                                    <div class="mt-5" v-else-if="field.type === 'textarea'">
                                        <b-field>
                                            <template #label>{{ field.title }} <span class="required" v-if="field.is_required">*</span></template>
                                            <b-input type="textarea" v-model="field.value" :required="field.is_required === 1"></b-input>
                                            <template #message><span v-html="field.help"></span></template>
                                        </b-field>
                                    </div>
                                </div>
                            </div>

                            <div v-if="conference.is_access_only || conference.feeRange[1] > 0">
                                <h4 class="mt-6 mb-4">Access<span v-if="conference.feeRange[1] > 0">/Discount</span> Code <span class="required" v-if="conference.is_access_only">*</span></h4>
                                <div class="help mt-0 mb-5" v-if="conference.is_access_only">Access to this conference is only available by access code.  Apply your code below to allow registration.</div>

                                <div class="columns" v-if="!discount.title">
                                    <div class="column">
                                        <b-field :message="(conference.is_access_only) ? '' : 'Optional'">
                                            <b-input v-model="registration.discount"/>
                                        </b-field>
                                    </div>
                                    <div class="column">
                                        <b-button type="is-info" @click="applyDiscount">Apply Code</b-button>
                                    </div>
                                </div>

                                <div class="columns" v-else>
                                    <div class="column">
                                        <b-message type="is-success" variant="success" v-if="discount.type === 'amount'">
                                            <i class="fas fa-check-circle mr-2"></i>{{ discount.title }}<span v-if="conference.feeRange[1] > 0">: {{ discount.value | currency }} Discount</span>
                                            <b-button size="sm" variant="success" style="float: right;" @click="removeDiscount">Remove</b-button>
                                        </b-message>
                                        <b-message type="is-success" v-else-if="discount.type === 'percent'">
                                            <i class="fas fa-check-circle mr-2"></i>{{ discount.title }}<span v-if="conference.feeRange[1] > 0">: {{ discount.value.toFixed(2) }}% Discount</span>
                                            <b-button size="sm" variant="success" style="float: right;" @click="removeDiscount">Remove</b-button>
                                        </b-message>
                                    </div>
                                </div>

                                <div class="columns mt-2" v-if="discount.error">
                                    <div class="column">
                                        <b-message type="is-warning"><i class="fas fa-exclamation-circle mr-2"></i>{{ discount.error }}</b-message>
                                    </div>
                                </div>
                            </div>

                            <div v-if="registration.regType && registration.regType.fee > 0 && chargeAmount > 0">
                                <h4 class="mt-6 mb-4">Payment</h4>

                                <div class="columns mt-5">
                                    <div class="column">
                                        <div>
                                            <b-radio v-model="registration.paymentType" value="cc" native-value="cc" @click="stripeInit">
                                                Pay with <strong>Credit Card</strong>
                                            </b-radio>
                                        </div>
                                        <div class="mt-2">
                                            <b-radio v-model="registration.paymentType" value="po" native-value="po" v-if="conference.is_allow_po">
                                                Pay with <strong>Purchase Order (P.O.)</strong>
                                            </b-radio>
                                        </div>
                                    </div>

                                </div>

                                <div v-show="clientSecret && registration.paymentType === 'cc'" class="pb-3">
                                    <hr/>
                                    <div class="columns is-gapless">
                                        <div class="column is-narrow"><i class="far fa-info-circle mr-4"></i></div>
                                        <div class="column">
                                            <p>Your card will be charged <strong>{{ chargeAmount | currency }}</strong> for: <strong>{{ registration.regType.title }}</strong>, with successful registration.</p>
                                            <p v-if="registration.regType.cc_surcharge_info"><span class="text-muted" style="font-size: 0.9rem">Registration  includes a {{ registration.regType.cc_surcharge_info | currency }} registration processing fee.</span></p>

                                            <!-- Tax breakdown -->
                                            <div v-if="taxAmount > 0" class="mt-5 mb-4" style="background-color: #f8f9fa; padding: 1rem; border-radius: 4px; border: 1px solid #e0e2e8;">
                                                <table style="width: 100%; font-size: 0.95rem; border-collapse: collapse;">
                                                    <tr>
                                                        <td style="padding-bottom: 0.25rem; border: none;">Registration Fee:</td>
                                                        <td style="text-align: right; padding-bottom: 0.25rem; border: none;">{{ baseAmount | currency }}</td>
                                                    </tr>
                                                    <tr v-if="taxDetails">
                                                        <td style="padding-bottom: 0.25rem; border: none;">Sales Tax ({{ (taxDetails.taxRate * 100).toFixed(2) }}%, {{ taxDetails.jurisdiction }}):</td>
                                                        <td style="text-align: right; padding-bottom: 0.25rem; border: none;">{{ taxAmount | currency }}</td>
                                                    </tr>
                                                    <tr v-else>
                                                        <td style="padding-bottom: 0.25rem; border: none;">Sales Tax:</td>
                                                        <td style="text-align: right; padding-bottom: 0.25rem; border: none;">{{ taxAmount | currency }}</td>
                                                    </tr>
                                                    <tr style="font-weight: 600;">
                                                        <td style="padding-top: 0.5rem; border: none; border-top: 1px solid #dee2e6;">Total:</td>
                                                        <td style="text-align: right; padding-top: 0.5rem; border: none; border-top: 1px solid #dee2e6;">{{ chargeAmount | currency }}</td>
                                                    </tr>
                                                </table>
                                            </div>

                                            <div class="mt-3">
                                                <div class="card-container">
                                                    <div ref="card"></div>
                                                    <b-message type="is-danger" v-show="stripeErrors" class="mt-6">
                                                        <div id="card-errors"><i class="far fa-exclamation-triangle mr-3"></i>{{ stripeErrors }}</div>
                                                    </b-message>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div v-if="registration.paymentType === 'cc' && !clientSecret" class="pb-3"><i class="fas fa-spin fa-spinner fa-fw mr-3"></i>Establishing secure connection for Credit Card...</div>

                                <div v-show="registration.paymentType === 'po'">
                                    <hr/>
                                    <div class="columns is-gapless">
                                        <div class="column is-narrow"><i class="far fa-info-circle mr-4"></i></div>
                                        <div class="column">
                                            <p>You opted to use a Purchase Order (P.O.) for your registration. The charge is <strong>{{ poAmount | currency }}</strong> for: <strong>{{ registration.regType.title }}</strong>.</p>
                                        </div>
                                    </div>
                                    <div class="columns is-gapless">
                                        <div class="column is-narrow"><i class="far fa-exclamation-triangle has-text-danger mr-4"></i></div>
                                        <div class="column" v-if="conference.is_allow_invoice">
                                            <p>Immediately following submitting your registration you can generate a P.O. Request which <u>you</u> may need to submit for a purchase order. Carefully follow any instructions on the P.O. request and follow through on this process. Your registration will not be complete until the purchase order process is complete. Please allow enough time before the conference for this process including up to 48 hours to process and confirm your registration. Please contact the event organizer if you have any questions.</p>
                                            <p class="mt-2">
                                                <b-switch v-model="poReady">I understand that I must submit a purchase order to complete this registration.</b-switch>
                                            </p>
                                        </div>
                                        <div class="column" v-else>
                                            <p>Your registration will not be complete until the purchase order process is complete. Please allow enough time before the conference for this process including up to 48 hours to process and confirm your registration. Please contact the event organizer if you have any questions.</p>
                                            <p class="mt-2">
                                                <b-switch v-model="poReady">I understand that I'm having this registration paid by a purchase order.</b-switch>
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <hr class="mt-6" />

                            <b-button size="is-large" type="is-primary" expanded class="mt-6" @click="register" :disabled="!isValid || processing"><i class="fas fa-check mr-4"></i>Submit Registration</b-button>

                            <div v-if="!isValid && validationMessage.length > 0" class="mt-4" style="background-color: #fff3cd; border: 1px solid #ffc107; border-radius: 4px; padding: 1rem;">
                                <div style="font-weight: 600; margin-bottom: 0.5rem; color: #856404;"><i class="fas fa-exclamation-triangle mr-2"></i>Please complete the following to submit registration:</div>
                                <ul style="margin: 0; padding-left: 1.5rem; color: #856404;">
                                    <li v-for="message in validationMessage" :key="message" style="margin-bottom: 0.25rem;">{{ message }}</li>
                                </ul>
                            </div>
                        </div>
                    </section>

                    <section v-else>
                        <div class="pt-4">
                            <div class="columns">
                                <div class="column is-narrow">
                                    <i class="far fa-check-circle fa-3x has-text-primary"></i>
                                </div>
                                <div class="column is-9 content">
                                    <h3 class="mt-3">Thank you for registering.</h3>

                                    <p class="mt-6">You can access your registration from your <strong><i class="far fa-screen-users mr-2 ml-1"></i>Conference Records</strong> at any time. If you pre-registered, additional information for this event may be made available to you as the event approaches via e-mail or on the conference information screen.</p>

                                    <div class="mt-5 mb-4" v-if="conference.support_email">
                                        Please, <a :href="'mailto:' + conference.support_email + '?subject=' + conference.title">contact the event organizer</a> if you have any questions about this event or need assistance.
                                    </div>

                                    <div class="mt-6">
                                        <router-link to="/registrations">
                                            <b-button type="is-primary">
                                                <span class="is-hidden-mobile">Continue to your <i class="far fa-screen-users mr-2 ml-1"></i>Conference Records<i class="far fa-long-arrow-right ml-3"></i></span>
                                                <span class="is-hidden-desktop"><i class="far fa-screen-users mr-2 ml-1"></i>Conference Records<i class="far fa-long-arrow-right ml-3"></i></span>
                                            </b-button>
                                        </router-link>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>
                </div>
            </div>

        </div>
    </div>
</template>

<script>
    import {client as http, stripePK} from "../../http_client";
    import {mapGetters} from "vuex";
    import moment from "moment";
    import async from "async";

    let stripe = Stripe(stripePK),
        elements = stripe.elements(),
        card = undefined;

    export default {
        name: 'login',
        components: {},
        props: ['confId'],
        data: function() {
            return {
                conference: {
                    feeRange: [0, 0],
                    registered: false
                },
                registration: {
                    paymentType: null,
                    questions: [],
                    regType: {},
                    discount: null // v-model code
                },
                discount: { // server return
                    error: null
                },
                stripeErrors: null, // stripe errors
                stripeInitialized: false,
                ccReady: false,
                poReady: false,
                accessReady: false,
                processing: false,
                clientSecret: null,
                intentId: null,
                intentCharge: 0,
                taxAmount: 0,
                baseAmount: 0,
                taxDetails: null,
            };
        },
        computed: {
            ...mapGetters(['currentUser']),
            fees: function() {
                if (this.conference.feeRange[0]) {
                    if (this.conference.feeRange[0] === this.conference.feeRange[1]) {
                        return '$' + this.conference.feeRange[0].toFixed(2);
                    } else {
                        return '$' + this.conference.feeRange[0].toFixed(2) + ' - ' + '$' + this.conference.feeRange[1].toFixed(2);
                    }
                } else {
                    return 'N/A';
                }
            },
            confDates: function() {
                let s = moment(this.conference.start_at);
                let e = moment(this.conference.end_at);
                if (s.isSame(e, 'day')) {
                    return s.format('MMMM D, YYYY');
                } else if (s.isSame(e, 'month')) {
                    return s.format('MMMM D - ') + e.format('D, YYYY');
                } else if (s.isSame(e, 'year')) {
                    return s.format('MMM D - ') + e.format('MMM D, YYYY');
                } else {
                    return s.format('MMM D, YYYY - ') + e.format('MMM D, YYYY');
                }
            },
            regDates: function() {
                let s = moment(this.conference.open_at);
                let e = moment(this.conference.close_at);
                if (s.isSame(e, 'day')) {
                    return s.format('MMMM D, YYYY');
                } else if (s.isSame(e, 'month')) {
                    return s.format('MMMM D - ') + e.format('D, YYYY');
                } else if (s.isSame(e, 'year')) {
                    return s.format('MMM D - ') + e.format('MMM D, YYYY');
                } else {
                    return s.format('MMM D, YYYY - ') + e.format('MMM D, YYYY');
                }
            },
            closed: function() {
                return (moment().isBefore(this.conference.open_at) || moment().isAfter(this.conference.close_at) || this.conference.regTypes.length === 0);
            },
            poAmount: function() {
                // For PO payments: only the base registration fee, with discount applied if applicable
                if (this.registration.regType && this.registration.regType.fee) {
                    let charge = this.registration.regType.fee;
                    if (this.discount.value) {
                        if (this.discount.type === 'percent') {
                            charge = charge - Math.floor(this.registration.regType.fee * (this.discount.value / 100));
                        } else {
                            charge = charge - this.discount.value;
                        }
                    }
                    return charge;
                } else {
                    return 0;
                }
            },
            chargeAmount: function() {
                // For CC payments, use the intent charge which includes surcharge and tax
                if (this.registration.paymentType === 'cc' && this.intentCharge) {
                    return (this.intentCharge / 100);
                }

                // For PO payments, use the poAmount (no surcharge, no tax)
                if (this.registration.paymentType === 'po') {
                    return this.poAmount;
                }

                // Fallback for when intent hasn't loaded yet
                if (this.registration.regType && this.registration.regType.fee) {
                    let charge = this.registration.regType.fee;
                    if (this.discount.value) {
                        if (this.discount.type === 'percent') {
                            charge = charge - Math.floor(this.registration.regType.fee * (this.discount.value / 100));
                        } else {
                            charge = charge - this.discount.value;
                        }
                    }

                    // Add cc surcharge if CC selected but intent not loaded yet
                    if (this.registration.paymentType === 'cc') {
                        charge += this.registration.regType.cc_surcharge;
                    }

                    return charge;
                } else {
                    return 0;
                }
            },
            validationMessage: function() {
                let messages = [];

                if (!this.registration.regType.id) {
                    messages.push('Please select a registration type');
                }

                if (!this.registration.paymentType && this.chargeAmount > 0) {
                    messages.push('Please select a payment method');
                } else if (this.registration.paymentType === 'cc' && !this.ccReady) {
                    messages.push('Please complete credit card information');
                } else if (this.registration.paymentType === 'po' && !this.poReady) {
                    messages.push('Please confirm purchase order terms');
                } else if (this.registration.paymentType === 'access' && !this.discount.title) {
                    messages.push('Please apply a valid access code');
                }

                let unansweredQuestions = [];
                for (let i = 0; i < this.registration.questions.length; i++) {
                    const question = this.registration.questions[i];
                    if (question.is_required && !question.value) {
                        unansweredQuestions.push(question.title);
                    }
                }
                if (unansweredQuestions.length > 0) {
                    if (unansweredQuestions.length === 1) {
                        messages.push(`Please answer required question: ${unansweredQuestions[0]}`);
                    } else {
                        messages.push(`Please answer ${unansweredQuestions.length} required questions`);
                    }
                }

                return messages;
            },
            isValid: function() {
                let valid = true;

                if (!this.registration.paymentType) valid = false;
                if (this.registration.paymentType === 'cc' && !this.ccReady) valid = false;
                if (this.registration.paymentType === 'po' && !this.poReady) valid = false;
                if (this.registration.paymentType === 'access' && !this.discount.title) valid = false;

                if (!this.registration.paymentType && this.chargeAmount === 0) {
                    valid = true;
                }

                if (!this.registration.regType.id) valid = false;

                for (let i = 0; i < this.registration.questions.length; i++) {
                    const question = this.registration.questions[i];
                    if (question.is_required && !question.value) {
                        valid = false;
                        break;
                    }
                }

                return valid;
            }
        },
        methods: {
            loadConference: function(force) {
                this.processing = true;
                http.get('app/conferences/' + this.confId, {force}).then(response => {
                    this.conference = response.data;

                    this.registration.questions = [...response.data.questions];

                    if (this.conference.is_access_only) {
                        this.registration.paymentType = 'access';
                    }

                    if (this.conference.regTypes.length === 1) {
                        this.registration.regType = this.conference.regTypes[0];
                    }

                    this.processing = false;

                    // if it was included then lets check it
                    if (this.$route.query.code) {
                        this.registration.discount = this.$route.query.code;
                        this.applyDiscount();
                    }
                });
            },
            applyDiscount: function() {
                this.discount = {error: null};

                http.get('app/conferences/discount/' + this.conference.code + '/' + this.registration.discount + '/registration').then(response => {
                    this.discount = response.data;
                    this.intentCharge = 0;
                    this.taxAmount = 0;
                    this.baseAmount = 0;
                    this.taxDetails = null;
                    this.recalcIntent();

                    // if access only and they have a valid code, lets just let them in
                    if (this.conference.is_access_only) {
                        console.log('Access only conference, applying code and registering');
                        if (this.conference.questions.length === 0) {
                            this.register();
                        }
                    }
                }).catch(err => {
                    this.discount.error = err.response.data;
                });
            },
            removeDiscount: function() {
                this.registration.discount = null;
                this.discount = {error: null};
                this.intentCharge = 0;
                this.taxAmount = 0;
                this.baseAmount = 0;
                this.taxDetails = null;
                this.recalcIntent();
            },
            getPaymentIntent: function(force) {
                this.$emit('loading', true);
                http.post('app/conferences/intent', {conference_id: this.conference.id, reg_type_id: this.registration.regType.id, discount_id: this.discount.id}, {force}).then(response => {
                    this.intentId = response.data.id;
                    this.clientSecret = response.data.clientSecret;
                    this.intentCharge = response.data.total * 100;
                    this.baseAmount = response.data.baseAmount || 0;
                    this.taxAmount = response.data.taxAmount || 0;
                    this.taxDetails = response.data.taxDetails || null;
                });
            },
            recalcIntent() {
                if (this.registration.paymentType === 'cc') {
                    this.clientSecret = null;
                    this.getPaymentIntent();
                }
            },
            stripeInit() {
                this.getPaymentIntent();

                if (card) {
                    card.unmount();
                }

                card = elements.create('card');
                card.mount(this.$refs.card);

                card.on('change', event => {
                    if (event.error) {
                        this.stripeErrors = event.error.message;
                    } else {
                        this.stripeErrors = '';
                    }

                    this.ccReady = !!(event.complete);
                });
            },
            register() {
                if (!this.processing) {
                    this.processing = true;

                    async.series([
                        // Run card (if needed)
                        (cb) => {
                            if (this.registration.paymentType === 'cc') {
                                stripe.confirmCardPayment(this.clientSecret, {
                                    payment_method: {
                                        card: card,
                                        billing_details: {
                                            name: this.currentUser.first_name + ' ' + this.currentUser.last_name,
                                            email: this.currentUser.email
                                        }
                                    }
                                }).then(result => {
                                    if (result.error) {
                                        this.stripeErrors = result.error.message;
                                        cb(result.error.message);
                                    } else if (result.paymentIntent.status === 'succeeded') {
                                        cb();
                                    }
                                });
                            } else {
                                cb();
                            }
                        },
                        // complete registration
                        (cb) => {
                            let payload = {
                                conference_id: this.conference.id,
                                reg_type_id: this.registration.regType.id,
                                discount: this.registration.discount,
                                discount_id: this.discount.id,
                                intent_id: this.intentId,
                                total_paid: this.chargeAmount,
                                payment_type: this.registration.paymentType,
                                fields: []
                            };

                            for (let i = 0; i < this.registration.questions.length; i++) {
                                const field = this.registration.questions[i];
                                if (field.value) {
                                    payload.fields.push({
                                        id: field.id,
                                        value: field.value
                                    });
                                }
                            }

                            if (this.chargeAmount === 0) payload.payment_type = 'access';

                            http.post('app/conferences/register', payload).then(response => {
                                cb();
                            }).catch(err => {
                                if (err && err.response && err.response.data) { // is this even checking something real?
                                    console.log(err.response.data);
                                    // this.stripeErrors = err.response.data;
                                    window.alert('Registration failed.  Please try again or contact us at support@my247ed.com if you continue to have problems.');
                                    cb(err.response.data)
                                } else {
                                    this.processing = false;
                                    window.alert('Registration failed.  Please try again or contact us at support@my247ed.com if you continue to have problems.');
                                    cb(err);
                                }
                            });
                        }
                    ], err => {
                        if (err) {
                            console.log(err);
                        } else {
                            if (this.conference.is_access_only) {
                                console.log('Redirecting to registrations');
                                this.$router.push('/registrations');
                            } else {
                                console.log('Registration complete');
                                this.conference.registered = true;
                            }
                        }
                    });
                }
            },
            scrollTo(refName) {
                let element = this.$refs[refName];
                if (element) {
                    element.scrollIntoView({behavior: 'smooth'});
                }
            },
        },
        watch: {
            'registration.paymentType': function() {
                if (this.registration.paymentType === 'cc' && !this.stripeInitialized) {
                    this.stripeInitialized = true;
                    this.stripeInit();
                }
            },
            'registration.regType': function() {
                this.recalcIntent();
            }
        },
        mounted() {
            this.loadConference();
        }
    };
</script>

<style lang="scss" scoped>
    .conference {
        // TODO this should be replaced with ui-card
        background-color: white;
        border-radius: 4px;
        box-shadow: 5px 5px 30px 0 rgba(0, 0, 0, 0.4);
        padding: 3.7rem 3.7rem 6rem;
        margin-bottom: 6rem;

        @media (max-width: 1180px) {
            padding: 2rem;
            margin-top: 0;
            margin-bottom: 4rem;

        }

        @media (max-width: 768px) {
            padding: 1rem;
        }

        &.conf-info {
            position: relative;
            padding-bottom: 3.6rem;
            margin-top: 2rem;

            .logo-frame {
                position: absolute;
                right: 3.7rem;
                top: -160px;

                border: 2px solid #224C74;
                background: white;
                border-radius: 0.3rem;
                padding: 1rem;
                width: 200px;
                height: 200px;

                display: flex;
                align-content: center;
                align-items: center;

                img {
                    width: 100%;
                }

                @media (max-width: 768px) {
                    display: none;
                }
            }

            @media (max-width: 1180px) {
                padding: 2rem;
                margin: 0;
            }
        }

    }

    .details-box {
        background-color: #fdfdfd;
        border-radius: 8px;
        border: 1px solid #e0e2e8;

        .detail {
            padding: 0.5rem 1rem;
            border-bottom: 1px solid #e0e2e8;

            &:last-child {
                border-bottom: none;
            }

            .header {
                font-size: 1rem;
                color: #224C74;
                font-weight: bold;
            }

            .info {
                font-size: 1.1rem;
            }
        }
    }

    .closed-box {
        margin-top: 3rem;
        background-color: #ffddaa;
        padding: 1rem 2rem;
        border-radius: 8px;

        .meta {
            font-size: 0.85rem;
            color: #796d61;
        }
    }

    .card-container {
        border: 1px solid #e7e7e7;
        border-radius: 8px;
        padding: 2rem;
        margin-bottom: 2rem;
    }

    .errors-container {
        border: 2px solid red;
        border-radius: 8px;
        padding: 1rem 2rem;
        margin-top: 2rem;
    }
</style>

<style>
    .required {
        color: red;
    }
</style>
